import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Space as BaseSpace,
  Typography,
} from 'antd';
import { InputProps } from 'antd/lib/input';
import { SpaceProps } from 'antd/lib/space';
import classNames from 'classnames';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import { useRaf } from 'react-use';
import ImageViewer from '../../components/ImageViewer';
import NotFound from '../../components/NotFound';
import { OnetrackProvider } from '../../components/Onetrack/context';
import TrackClick from '../../components/Onetrack/TrackClick';
import TrackView from '../../components/Onetrack/TrackView';
import PicturesWall from '../../components/PicturesWall';
import useAsyncFn from '../../hooks/useAsyncFn';
import useCatchError from '../../hooks/useCatchError';
import CardLayout from '../../layouts/CardLayout';
import {
  commit,
  CommitParams,
  ErrorMessage,
  HelpText,
  requestCategory,
  requestType,
  sendMailCode,
} from '../../service/support';
import fallback from '../../utils/fallback';
import { Link, useLocale } from '../../utils/i18n';
import BeforeStart from './BeforeStart';
import RoundButton from './Button';
import ContactCategory from './Category';
import CategoryRadio from './CategoryRadio';
import { ContactContext, useContact } from './context';
import ContactDPO from './DPO';
import styles from './index.module.less';
import ContactMeta from './Meta';
import EmailValidator from 'email-validator';

const ImageRender: React.FC<React.HTMLAttributes<HTMLImageElement>> = (
  props
) => <ImageViewer {...props} />;

const EmailInput: React.FC<InputProps> = (props) => {
  const intl = useIntl();
  return (
    <>
      <Typography.Text
        type="secondary"
        style={{
          display: 'block',
          position: 'relative',
          top: -10,
          marginBottom: 2,
        }}
      >
        <FormattedMessage id="Contact.Notice.Email" />
      </Typography.Text>
      <Input
        {...props}
        style={{ maxWidth: '500px', width: '100%' }}
        placeholder={intl.formatMessage({ id: 'Contact.Placeholder.Email' })}
      />
    </>
  );
};

const Space: React.FC<SpaceProps> = ({ children, ...rest }) => (
  <Row>
    <Col sm={0} xs={24}>
      {React.Children.map(children, (child) => child)?.reverse()}
    </Col>
    <Col sm={24} xs={0}>
      <BaseSpace {...rest}>{children}</BaseSpace>
    </Col>
  </Row>
);

const ResendCountdown: React.FC<{ onEnd: () => void; countdown: number }> = ({
  onEnd,
  countdown,
}) => {
  const ms = countdown * 1000;
  const elapsed = useRaf(ms);
  const onEndRef = React.useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    const timer = setTimeout(() => {
      onEndRef.current();
    }, ms);

    return () => {
      clearTimeout(timer);
    };
  }, [ms]);

  return (
    <Button disabled>
      <FormattedMessage
        id="Contact.Button.ResendCaptcha"
        values={{ n: (countdown * (1 - elapsed)).toFixed() }}
      />
    </Button>
  );
};

const EmailCaptcha: React.FC<InputProps & { email: () => Promise<string> }> = ({
  email,
  className,
  style,
  ...rest
}) => {
  const intl = useIntl();
  const [locale] = useLocale();
  const [disabled, setDisabled] = useState(false);
  const [result, fetch] = useAsyncFn(
    useCatchError(async () => {
      const mail = await email();
      if (!mail) {
        return;
      }
      await sendMailCode({ mail, locale });
      setDisabled(true);
    })
  );

  return (
    <>
      <BaseSpace className={className} style={style}>
        <Input
          {...rest}
          placeholder={intl.formatMessage({
            id: 'Contact.Placeholder.Captcha',
          })}
        />
        {disabled && (
          <ResendCountdown countdown={60} onEnd={() => setDisabled(false)} />
        )}
        {!disabled && (
          <TrackClick tip="71.7.2.1.2171">
            <Button loading={result.loading} onClick={fetch}>
              <FormattedMessage id="Contact.Button.Captcha" />
            </Button>
          </TrackClick>
        )}
      </BaseSpace>
      {result.initialized && (
        <Typography.Text
          type="secondary"
          style={{
            display: 'block',
            marginTop: 4,
          }}
        >
          <FormattedMessage id="Contact.Notice.Captcha" />
        </Typography.Text>
      )}
    </>
  );
};

const AccountInput: React.FC<InputProps> = (props) => {
  const intl = useIntl();
  return (
    <>
      <Typography.Text
        type="secondary"
        style={{
          display: 'block',
          position: 'relative',
          top: -10,
          marginBottom: 2,
        }}
      >
        <FormattedMessage id="Contact.Notice.Account" />
      </Typography.Text>
      <Input
        {...props}
        style={{ maxWidth: '500px', width: '100%' }}
        placeholder={intl.formatMessage({
          id: 'Contact.Placeholder.Account',
        })}
      />
    </>
  );
};

const ContactFill: React.FC = () => {
  const intl = useIntl();
  const [next, setNext] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  const { values, category, type } = useContact();

  const history = useHistory();
  const [form] = Form.useForm();
  const [locale] = useLocale();
  const [result, submit] = useAsyncFn(commit);

  const { search } = history.location;

  const { regionCode, requestCategoryId, requestTypeId } = values;
  if (!(regionCode && requestCategoryId && requestTypeId)) {
    return <Redirect to={{ pathname: '/contact/type', search }} />;
  }

  if (result.initialized) {
    return (
      <TrackView tip="71.7.4.1.2179">
        <CardLayout
          key="success"
          title={<FormattedMessage id="Contact.Privacy.Team" />}
          centered
        >
          <Result
            className={styles.result}
            status="success"
            title={<FormattedMessage id="Contact.Success.Title" />}
            subTitle={<FormattedMessage id="Contact.Success.Content" />}
            extra={
              <Link to="/">
                <RoundButton className={styles.button}>
                  <FormattedMessage id="NotFound.Button.BackHome" />
                </RoundButton>
              </Link>
            }
          />
        </CardLayout>
      </TrackView>
    );
  }

  if (result.error) {
    return (
      <TrackView tip="71.7.5.1.2180">
        <CardLayout
          key="failure"
          title={<FormattedMessage id="Contact.Privacy.Team" />}
          centered
        >
          <Result
            className={styles.result}
            status="error"
            title={<FormattedMessage id="Contact.Failure.Title" />}
            subTitle={
              <FormattedMessage
                id="Contact.Failure.Content"
                values={{ message: <ErrorMessage error={result.error} /> }}
              />
            }
            extra={
              <RoundButton className={styles.button} onClick={result.reset}>
                <FormattedMessage id="Contact.Button.Back" />
              </RoundButton>
            }
          />
        </CardLayout>
      </TrackView>
    );
  }

  const confirm = (
    <Modal
      className={classNames('ant-modal-confirm', styles.confirm)}
      width={650}
      centered
      visible={visible}
      closable={false}
      footer={null}
    >
      <TrackView tip="71.7.3.1.2175">
        <div className="ant-modal-confirm-body-wrapper">
          <div className="ant-modal-confirm-body">
            <span className="ant-modal-confirm-title">
              <FormattedMessage id="Contact.Confirm.Title" />
            </span>
            <div className="ant-modal-confirm-content">
              <FormattedMessage
                id="Contact.Confirm.Content"
                values={{
                  service: (
                    <TrackClick tip="71.7.3.1.2178">
                      <Link to={`/service#${regionCode}`} target="_blank">
                        <FormattedMessage id="Service" />
                      </Link>
                    </TrackClick>
                  ),
                }}
              />
            </div>
          </div>
          <div className="ant-modal-confirm-btns">
            <TrackClick tip="71.7.3.1.2177">
              <RoundButton
                className={styles.action}
                onClick={() => setVisible(false)}
              >
                <FormattedMessage id="Contact.Button.Back" />
              </RoundButton>
            </TrackClick>
            <TrackClick tip="71.7.3.1.2176">
              <RoundButton
                className={styles.action}
                type="primary"
                onClick={() => {
                  setVisible(false);
                  const data = form.getFieldsValue();
                  submit({ ...values, ...data } as CommitParams);
                }}
              >
                <FormattedMessage id="Contact.Button.Submit" />
              </RoundButton>
            </TrackClick>
          </div>
        </div>
      </TrackView>
    </Modal>
  );

  return (
    <TrackView tip="71.7.1.1.2166">
      <CardLayout
        loading={category.loading || type.loading}
        title={<FormattedMessage id="Contact.Privacy.Team" />}
        meta={<ContactMeta />}
      >
        {confirm}
        <Form
          form={form}
          className={styles.form}
          layout="vertical"
          onFinish={() => setVisible(true)}
        >
          <div className={styles.legend}>
            <div className={styles.title}>
              <FormattedMessage id="Contact.Problem.Title" />
            </div>
            <div className={styles.subTitle}>
              <FormattedMessage id="Contact.Problem.Subtitle" />
            </div>
          </div>
          <Form.Item
            name="product"
            label={<FormattedMessage id="Contact.Label.Service" />}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'Message.Required' }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'Contact.Placeholder.Service',
              })}
            />
          </Form.Item>
          <Form.Item
            name="summary"
            label={<FormattedMessage id="Contact.Label.Title" />}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'Message.Required' }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'Contact.Placeholder.Title',
              })}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={<FormattedMessage id="Contact.Label.Content" />}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'Message.Required' }),
              },
              {
                min: 20,
                message: intl.formatMessage({ id: 'Message.Content.Less20' }),
              },
            ]}
          >
            <Input.TextArea
              placeholder={intl.formatMessage({
                id: 'Contact.Placeholder.Content',
              })}
              rows={6}
            />
          </Form.Item>
          <Form.Item
            name="temporaryAttachmentId"
            label={<FormattedMessage id="Contact.Label.Image" />}
          >
            <PicturesWall requestCategoryId={requestCategoryId} />
          </Form.Item>
          {next && (
            <TrackView tip="71.7.2.1.2170">
              <div className={styles.legend}>
                <div className={styles.title}>
                  <FormattedMessage id="Contact.Detail.Title" />
                </div>
                <div className={styles.subTitle}>
                  <FormattedMessage id="Contact.Detail.Subtitle" />
                </div>
              </div>
              <Form.Item
                name="userName"
                label={<FormattedMessage id="Contact.Label.Name" />}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'Message.Required' }),
                  },
                ]}
              >
                <Input
                  style={{ maxWidth: '500px', width: '100%' }}
                  placeholder={intl.formatMessage({
                    id: 'Contact.Placeholder.Name',
                  })}
                />
              </Form.Item>
              <Form.Item
                name="mail"
                label={<FormattedMessage id="Contact.Label.Email" />}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'Message.Required' }),
                  },
                  {
                    validator: (_, value, callback) => {
                      if (!value) {
                        callback();
                      }
                      if (EmailValidator.validate(value)) {
                        callback();
                      } else {
                        callback(
                          intl.formatMessage({
                            id: 'Message.Email.Invalid',
                          })
                        );
                      }
                    },
                    message: intl.formatMessage({
                      id: 'Message.Email.Invalid',
                    }),
                  },
                ]}
              >
                <EmailInput />
              </Form.Item>
              <Form.Item
                name="verifyCode"
                label={<FormattedMessage id="Contact.Label.Captcha" />}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'Message.Required' }),
                  },
                ]}
              >
                <EmailCaptcha
                  email={async () => {
                    try {
                      await form.validateFields(['mail']);
                      return form.getFieldValue('mail');
                    } catch {
                      return '';
                    }
                  }}
                  style={{ maxWidth: '500px', width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="account"
                label={<FormattedMessage id="Contact.Label.Account" />}
              >
                <AccountInput />
              </Form.Item>
              <Divider />
              <div>
                <span>
                  <FormattedMessage id="Contact.Privacy.Agreement" />
                </span>
                <a
                  href={`https://privacy.mi.com/all/${
                    locale === 'en'
                      ? 'en_US'
                      : locale.replace(
                          /-([a-z]+)$/,
                          (_, s) => `_${s.toUpperCase()}`
                        )
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({ id: 'Contact.Privacy.Link' })}
                </a>
              </div>
            </TrackView>
          )}
          <Form.Item>
            <Space>
              {!next && (
                <TrackClick tip="71.7.1.1.2169">
                  <RoundButton
                    className={styles.button}
                    onClick={() =>
                      history.push({ pathname: '/contact/suggestion', search })
                    }
                  >
                    <FormattedMessage id="Contact.Button.Back" />
                  </RoundButton>
                </TrackClick>
              )}
              {next && (
                <TrackClick tip="71.7.2.1.2174">
                  <RoundButton
                    className={styles.button}
                    onClick={() =>
                      history.push({ pathname: '/contact/suggestion', search })
                    }
                  >
                    <FormattedMessage id="Contact.Button.Back" />
                  </RoundButton>
                </TrackClick>
              )}
              {!next && (
                <TrackClick tip="71.7.1.1.2167">
                  <RoundButton
                    key="next"
                    className={styles.button}
                    type="primary"
                    onClick={async () => {
                      await form.validateFields();
                      setNext(true);
                    }}
                  >
                    <FormattedMessage id="Contact.Button.Next" />
                  </RoundButton>
                </TrackClick>
              )}
              {next && (
                <TrackClick tip="71.7.2.1.2172">
                  <RoundButton
                    key="submit"
                    className={styles.button}
                    type="primary"
                    htmlType="submit"
                    loading={result.loading}
                  >
                    <FormattedMessage id="Contact.Button.Submit" />
                  </RoundButton>
                </TrackClick>
              )}
            </Space>
          </Form.Item>
        </Form>
      </CardLayout>
    </TrackView>
  );
};

const ContactType: React.FC = () => {
  const intl = useIntl();
  const { type, values } = useContact();

  const [locale] = useLocale();
  const history = useHistory();

  const { regionCode, requestCategoryId } = values;

  if (!(regionCode && requestCategoryId)) {
    return <Redirect to="/contact/category" />;
  }

  return (
    <TrackView tip="71.5.0.1.2160">
      <CardLayout
        loading={type.loading}
        meta={<ContactMeta />}
        title={<FormattedMessage id="Contact.Privacy.Team" />}
      >
        <Form
          className={styles.form}
          layout="vertical"
          initialValues={values}
          onFinish={(values) => {
            const params = new URLSearchParams(history.location.search);
            params.set('requestTypeId', values.requestTypeId);
            history.push({
              pathname: '/contact/suggestion',
              search: `?${params}`,
            });
          }}
        >
          <Form.Item
            name="requestTypeId"
            label={<FormattedMessage id="Contact.Label.Category" />}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'Message.Category.Required',
                }),
              },
            ]}
          >
            {fallback(type, (items) => (
              <CategoryRadio
                options={items.sort().map(({ requestTypeId, resource }) => ({
                  value: requestTypeId,
                  title: resource.locales[locale].name,
                  description: resource.locales[locale].description,
                }))}
              />
            ))}
          </Form.Item>
          <TrackClick tip="71.5.0.1.2161">
            <RoundButton
              className={styles.button}
              type="primary"
              htmlType="submit"
            >
              <FormattedMessage id="Contact.Button.Next" />
            </RoundButton>
          </TrackClick>
        </Form>
      </CardLayout>
    </TrackView>
  );
};

const Suggestion: React.FC<{ helpText: HelpText }> = ({ helpText }) => {
  const history = useHistory();
  const { search } = history.location;

  return (
    <TrackView tip="71.6.0.1.2162">
      <div className={styles.legend}>
        <div className={styles.title}>
          <FormattedMessage id="Contact.Faq.Title" />
        </div>
        <div className={styles.subTitle}>
          <FormattedMessage id="Contact.Faq.Subtitle" />
        </div>
      </div>
      <ReactMarkdown
        className={styles.content}
        source={helpText.content}
        renderers={{
          image: (props) => <ImageRender {...props} />,
        }}
      />
      <Divider />
      <div className={styles.subTitle}>
        <FormattedMessage id="Contact.Faq.Not.Helpful" />
      </div>
      {!helpText.buttonUrl ? (
        <TrackClick tip="71.6.0.1.2165">
          <RoundButton
            className={styles.button}
            type="primary"
            onClick={() => history.push({ pathname: '/contact/fill', search })}
          >
            {helpText.button}
          </RoundButton>
        </TrackClick>
      ) : (
        <TrackClick tip="71.6.0.1.2164">
          <RoundButton
            href={helpText.buttonUrl}
            target="_blank"
            className={styles.button}
            type="primary"
          >
            {helpText.button}
          </RoundButton>
        </TrackClick>
      )}
    </TrackView>
  );
};

const ContactSuggestion: React.FC = () => {
  const { values, category, type } = useContact();

  const [locale] = useLocale();
  const history = useHistory();
  const { search } = history.location;

  const { regionCode, requestCategoryId, requestTypeId } = values;
  if (!(regionCode && requestCategoryId && requestTypeId)) {
    return <Redirect to={{ pathname: '/contact/type', search }} />;
  }

  return (
    <CardLayout
      loading={category.loading || type.loading}
      meta={<ContactMeta />}
      title={<FormattedMessage id="Contact.Privacy.Team" />}
    >
      {fallback(type, (items) => {
        const finded = items.find(
          (item) => item.requestTypeId === requestTypeId
        );
        if (!finded) {
          return null;
        }
        if (finded.helpText) {
          const helpText = finded.helpText[locale];
          return <Suggestion helpText={helpText} />;
        }
        return <Redirect to={{ pathname: '/contact/fill', search }} />;
      })}
    </CardLayout>
  );
};

const ContactPage: React.FC = () => {
  const [category, fetchCategory] = useAsyncFn(requestCategory);
  const [type, fetchType] = useAsyncFn(requestType);
  const history = useHistory();

  const { search } = history.location;

  const values = React.useMemo(() => qs.parse(search.substr(1)), [search]);

  React.useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  const { regionCode, requestCategoryId, requestTypeId } = values;

  React.useEffect(() => {
    if (regionCode && requestCategoryId) {
      fetchType({ requestCategoryId, regionCode });
    }
  }, [regionCode, requestCategoryId, fetchType]);

  const match = useRouteMatch();

  if (category.error || type.error) {
    const error = category.error || type.error;
    return (
      <CardLayout
        key="failure"
        title={<FormattedMessage id="Contact.Privacy.Team" />}
        centered
      >
        <Result
          className={styles.result}
          status="error"
          title={error.name}
          subTitle={<ErrorMessage error={error} />}
          extra={
            <RoundButton
              className={styles.button}
              onClick={() => {
                category.retry();
                type.retry();
              }}
            >
              <FormattedMessage id="Contact.Button.Retry" />
            </RoundButton>
          }
        />
      </CardLayout>
    );
  }

  return (
    <OnetrackProvider
      params={{
        region_code: regionCode,
        request_category_id: requestCategoryId,
        request_type_id: requestTypeId,
      }}
    >
      <ContactContext.Provider value={{ category, type, values }}>
        <Switch>
          <Route exact path={match.path}>
            <BeforeStart />
          </Route>
          <Route path={`${match.path}/dpo`}>
            <ContactDPO />
          </Route>
          <Route path={`${match.path}/category`}>
            <ContactCategory />
          </Route>
          <Route path={`${match.path}/type`}>
            <ContactType />
          </Route>
          <Route path={`${match.path}/suggestion`}>
            <ContactSuggestion />
          </Route>
          <Route path={`${match.path}/fill`}>
            <ContactFill />
          </Route>
          <Route path={match.path}>
            <NotFound />
          </Route>
        </Switch>
      </ContactContext.Provider>
    </OnetrackProvider>
  );
};

export default ContactPage;
